import { Close, Kitchen, TableRestaurant } from '@mui/icons-material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { DataGrid, GridRowId } from '@mui/x-data-grid'
import React, { useEffect, useRef, useState } from 'react'
import { BillItem, ITables, KITCHEN_ITEM_STATUS } from '../../all-types'
import { CommonBtn } from '../../components'
import { useTableByIdLive } from '../../realtime'
import { mergeLiveTable, setSelectedTable } from '../../redux-store'
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks'
import {
  getTableById,
  removeTableItem,
  updateCartItemOfTheTable,
  updateTableItemStatus,
} from '../../services'
import { getKitchenItemStatusInfo } from '../../utils'
import { MODIFY_BILL_TABLE_ID, TAKE_AWAY_TABLE_ID } from '../../constants'

interface CartPanelProps {
  handleItemEdit: (productId: string, editIndex: number) => void
}

const ProductsCartItems: React.FC<CartPanelProps> = ({ handleItemEdit }) => {
  const theme = useTheme()
  const table = useAppSelector((state) => state.table.selectedTable)
  const pendingCartItemSnos = useAppSelector(
    (state) => state.table.pendingCartItemSnos
  )
  const dispatcher = useAppDispatch()

  const isTakeAwayTable = table?._id === TAKE_AWAY_TABLE_ID
  const isModifyBill =
    table?._id === MODIFY_BILL_TABLE_ID || table?.tableName === 'Modify Bill'
  const isLocalBill = isTakeAwayTable || isModifyBill
  const cartItems = table?.billSession?.items || []
  const [selectedRow, setSelectedRow] = useState<number | null>(null)
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  )
  const [selectedItems, setSelectedItems] = useState<GridRowId[]>([])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(5)
  const [selectedOption, setSelectedOption] = useState('')
  // Tracks per-item kitchen-status updates in flight. Using a map keyed by sno
  // so multiple rows can spin independently.
  const [pendingItemStatus, setPendingItemStatus] = useState<
    Record<number, KITCHEN_ITEM_STATUS>
  >({})
  // Per-item deletes in flight (sno -> true). Drives the delete spinner.
  const [pendingDeleteSno, setPendingDeleteSno] = useState<Record<number, boolean>>({})
  const pendingFallbackTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({})

  // Cart-wide delete lock. Only deletes block the entire panel because
  // they re-write the whole table document via PUT and rapid clicks were
  // racing each other and restoring just-deleted rows from stale
  // snapshots. Kitchen / Served status changes use a per-item endpoint
  // and have their own per-row guard, so they don't need to block the
  // rest of the cart.
  const isDeleting = Object.keys(pendingDeleteSno).length > 0
  const isDeletingRef = useRef(false)
  useEffect(() => {
    isDeletingRef.current = isDeleting
  }, [isDeleting])

  useTableByIdLive(table?._id, (t) => {
    // Ignore intermediate socket snapshots while a delete is in flight.
    // The server emits once per whole-table PUT, so an older snapshot
    // could briefly re-add an item the user just removed.
    if (isDeletingRef.current) return
    // Route the echo through the updatedAt merge instead of overwriting
    // directly, so a stale single-table snapshot can't revert newer item
    // state (P1-7).
    dispatcher(mergeLiveTable(t))
  })

  const refreshTableData = async () => {
    try {
      await dispatcher(getTableById(table?._id || ''))
    } catch (e) {
      console.error(e)
    }
  }

  // Calculate the total number of pages
  const totalPages = Math.ceil(cartItems.length / pageSize)

  const handleEdit = () => {
    try {
      if (selectedProductId && selectedRow) {
        handleItemEdit(selectedProductId, selectedRow - 1)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setSelectedRow(null)
    }
  }

  const handleDelete = async () => {
    // Hard guard — the panel overlay already blocks pointer events, but
    // also short-circuit here so any synthetic event (keyboard, etc.)
    // can't kick off a second delete while one is in flight.
    if (isDeletingRef.current) return
    if (!selectedRow || !table) return

    const editIndex = selectedRow - 1
    const sourceItems = table.billSession?.items || []
    const targetItem = sourceItems[editIndex]
    const sno = targetItem?.sno || 0

    const clonedTable: ITables = JSON.parse(JSON.stringify(table))
    const items = clonedTable.billSession?.items || []
    const deleteIndex = sno ? items.findIndex((item) => item.sno === sno) : editIndex
    if (deleteIndex < 0) {
      setSelectedRow(null)
      return
    }
    items.splice(deleteIndex, 1)
    if (clonedTable.billSession) {
      clonedTable.billSession = { ...clonedTable.billSession, items }
    }

    // Lock immediately, before any React state update / re-render. The overlay
    // is visual feedback, but this ref is the real click guard that blocks
    // rapid double/triple-clicks from firing additional deletes in the same
    // event burst.
    isDeletingRef.current = true
    if (sno) setPendingDeleteSno((prev) => ({ ...prev, [sno]: true }))
    setSelectedRow(null)

    // Optimistically remove the row so the user sees immediate feedback.
    await dispatcher(setSelectedTable(clonedTable))

    try {
      if (isLocalBill) {
        await dispatcher(setSelectedTable(clonedTable))
      } else if (table._id && sno) {
        // Remove just this one line item server-side instead of resending
        // the whole table, which would clobber other items' kitchen status.
        await dispatcher(removeTableItem({ tableId: table._id, sno }))
      }
    } catch (e) {
      console.log(e)
    } finally {
      if (sno) {
        setPendingDeleteSno((prev) => {
          if (!(sno in prev)) return prev
          const next = { ...prev }
          delete next[sno]
          return next
        })
      }
      isDeletingRef.current = false
    }
  }

  // Handle pagination
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1)
      setSelectedRow(null)
    }
  }

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1)
      setSelectedRow(null)
    }
  }

  useEffect(() => {
    setPage(totalPages - 1)
    setSelectedRow(null)
  }, [cartItems.length])

  //TODO CAN BE USED LATER
  const sendAllMarkedItemsToKitchen = async () => {
    try {
      if (table) {
        const clonedTable: ITables = JSON.parse(JSON.stringify(table))
        const billSession = clonedTable.billSession
        const sameTime = String(new Date().getTime())
        if (billSession) {
          const items = [...(billSession.items ?? [])]
          selectedItems?.forEach((selItem) => {
            const editIndex = Number(selItem) - 1
            if (
              editIndex !== null &&
              editIndex >= 0 &&
              editIndex < items.length
            ) {
              items[editIndex] = {
                ...items[editIndex],
                kitchInfo: {
                  ...items[editIndex].kitchInfo,
                  status: KITCHEN_ITEM_STATUS.IN_KITCHEN,
                  itemReceviedTime: sameTime,
                },
              }
            }
          })

          clonedTable.billSession = {
            ...billSession,
            items,
          }

          await dispatcher(setSelectedTable(clonedTable))
          if (!isLocalBill) {
            await dispatcher(updateCartItemOfTheTable({ table: clonedTable }))
          }
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  const setItemPending = (sno: number, status: KITCHEN_ITEM_STATUS | null) => {
    setPendingItemStatus((prev) => {
      const next = { ...prev }
      if (status === null) delete next[sno]
      else next[sno] = status
      return next
    })
  }

  const clearPendingTimer = (sno: number) => {
    const timer = pendingFallbackTimers.current[sno]
    if (timer) {
      clearTimeout(timer)
      delete pendingFallbackTimers.current[sno]
    }
  }

  // Once the actual cart items reflect the optimistic status, drop the
  // overlay. Without this, an out-of-order `tables:one`/`tables:list` socket
  // snapshot can revert the item visually after we cleared pending in
  // `finally`. See DineInKanban for the same pattern.
  useEffect(() => {
    if (Object.keys(pendingItemStatus).length === 0) return
    const settled: number[] = []
    for (const item of cartItems) {
      const expected = pendingItemStatus[item.sno]
      if (expected && item.kitchInfo?.status === expected) {
        settled.push(item.sno)
      }
    }
    if (settled.length === 0) return
    setPendingItemStatus((prev) => {
      const next = { ...prev }
      let changed = false
      for (const sno of settled) {
        if (sno in next) {
          delete next[sno]
          changed = true
        }
      }
      return changed ? next : prev
    })
    for (const sno of settled) clearPendingTimer(sno)
  }, [cartItems, pendingItemStatus])

  useEffect(
    () => () => {
      for (const t of Object.values(pendingFallbackTimers.current)) clearTimeout(t)
      pendingFallbackTimers.current = {}
    },
    []
  )

  const senItemToKitchen = async (status: KITCHEN_ITEM_STATUS) => {
    // Kitchen / Served changes only need to wait on an in-flight cart
    // *delete* (which rewrites the whole table). Status changes
    // themselves use a per-item endpoint and the per-sno `pendingItemStatus`
    // guard below already prevents double-clicks on the same row.
    if (isDeletingRef.current) return
    if (!table || !selectedRow) return
    const editIndex = selectedRow - 1
    const targetItem = cartItems[editIndex]
    const sno = targetItem?.sno || 0
    if (!sno) {
      setSelectedRow(null)
      return
    }
    if (pendingItemStatus[sno]) return // guard against double-click

    setItemPending(sno, status)
    setSelectedRow(null) // close the action overlay so the spinner is visible on the row

    clearPendingTimer(sno)
    pendingFallbackTimers.current[sno] = setTimeout(() => {
      setItemPending(sno, null)
      delete pendingFallbackTimers.current[sno]
    }, 5000)

    try {
      if (isLocalBill) {
        const clonedTable: ITables = JSON.parse(JSON.stringify(table))
        const items = clonedTable.billSession?.items || []
        const updateIndex = items.findIndex((item) => item.sno === sno)
        if (updateIndex >= 0) {
          items[updateIndex] = {
            ...items[updateIndex],
            kitchInfo: {
              ...items[updateIndex].kitchInfo,
              status,
            },
          }
          clonedTable.billSession = {
            ...clonedTable.billSession!,
            items,
          }
          await dispatcher(setSelectedTable(clonedTable))
        }
        return
      }

      await dispatcher(
        updateTableItemStatus({
          tableId: table?._id || '',
          sno,
          status,
        })
      )
    } catch (e) {
      console.error(e)
      clearPendingTimer(sno)
      setItemPending(sno, null)
    }
  }

  const columns = [
    {
      field: 'id',
      headerName: 'Cart',
      flex: 0.2,
      components: {
        Header: () => {
          return null
        },
      },
      renderCell: (params: any) => {
        const product: BillItem = params.row?.product
        const sno = product?.sno || 0
        const isStatusPending = Boolean(pendingItemStatus[sno])
        const isDeletePending = Boolean(pendingDeleteSno[sno])
        const isCartSavePending = Boolean(pendingCartItemSnos[sno])
        const isPending = isStatusPending || isDeletePending || isCartSavePending
        return (
          <Stack flexDirection={'row'} flex={1} gap={2} height={'100%'}>
            <Stack flex={2} justifyContent={'center'}>
              <Box
                sx={{
                  position: 'relative',
                  height: 50,
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundImage: `url(${product?.itemImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                {isPending && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.45)',
                    }}
                  >
                    <CircularProgress size={22} thickness={5} sx={{ color: '#fff' }} />
                  </Box>
                )}
              </Box>
            </Stack>
            <Stack flex={8} justifyContent={'center'}>
              <Stack>
                <Typography variant="body1">{product.itemName}</Typography>
              </Stack>
              <Stack flexDirection={'row'} justifyContent={'space-between'}>
                <Stack>
                  <Stack flexDirection={'row'} alignItems={'center'} gap={2}>
                    <Typography variant="body2">{`${product.quantity} ${product.quantityType}  `}</Typography>
                    <Typography variant="body2">
                      {` x ${product.noOfItems}`}{' '}
                    </Typography>
                  </Stack>
                  <Stack>
                    {/* <Typography variant='body2'>{`${product?.kitchInfo?.status || "In Cart"}`} </Typography> */}
                  </Stack>
                </Stack>
                <Stack>
                  <Typography variant="body2" fontWeight={'bold'}>
                    {product.amount}
                  </Typography>
                  <Chip
                    color={
                      (getKitchenItemStatusInfo(product?.kitchInfo?.status)
                        ?.color as any) || 'default'
                    }
                    size={'small'}
                    label={`${getKitchenItemStatusInfo(product?.kitchInfo?.status)?.displayText || 'In Cart'}`}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        )
      },
    },
    // { field: 'itemName', headerName: 'Item', flex: 4 },
    // { field: 'quantity', headerName: 'Qty', flex: 1.5 },
    // { field: 'amount', headerName: 'Amount', flex: 1.5 },
  ]

  const rows = cartItems
    ?.slice(page * pageSize, page * pageSize + pageSize) // Paginate the rows
    .map((item, index) => ({
      id: page * pageSize + index + 1, // Adjust index based on pagination
      itemName: item.itemName,
      quantity: item.quantity,
      amount: item.amount,
      productId: item.productId,
      product: item,
    }))

  const REDUCE = 40
  const ROW_HEIGHT = 120 - REDUCE

  const canShowSendInKitchen = (selectedRow: number) =>
    cartItems[selectedRow - 1] &&
    cartItems[selectedRow - 1]?.kitchInfo?.status ===
      KITCHEN_ITEM_STATUS.IN_CART

  const canShowServed = (selectedRow: number) =>
    cartItems[selectedRow - 1] &&
    (cartItems[selectedRow - 1]?.kitchInfo?.status ===
      KITCHEN_ITEM_STATUS.READY ||
      cartItems[selectedRow - 1]?.kitchInfo?.status ===
        KITCHEN_ITEM_STATUS.IN_CART)

  return (
    <Stack flex={1} sx={{ position: 'relative', width: '100%' }}>
      {/* Custom Pagination Controls */}
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, p: 1 }}
      >
        {/* <RefreshComponent onRefresh={refreshTableData} /> */}
        <Button
          onClick={handlePrevPage}
          disabled={page === 0}
          variant="contained"
          sx={{ width: 50, height: 50 }}
          startIcon={
            <KeyboardArrowLeftIcon
              fontSize="large"
              sx={{ color: 'white', marginLeft: 1 }}
            />
          }
        />

        <Typography sx={{ alignSelf: 'center' }}>
          {page + 1} of {totalPages}
        </Typography>
        <Button
          onClick={handleNextPage}
          disabled={page >= totalPages - 1}
          variant="contained"
          sx={{ width: 50, height: 50 }}
          startIcon={
            <KeyboardArrowRightIcon
              fontSize="large"
              sx={{ color: 'white', marginLeft: 1 }}
            />
          }
        />
        {/* <CommonBtn
                    onClick={handleNextPage}
                    disabled={page >= totalPages - 1}
                    variant="contained"
                >
                    Next
                </CommonBtn> */}
      </Box>

      <Stack>
        <DataGrid
          disableColumnResize
          rows={rows}
          columns={columns}
          rowHeight={ROW_HEIGHT}
          disableColumnSorting
          disableColumnFilter
          disableColumnMenu
          pageSizeOptions={[pageSize]} // Disable internal pagination
          hideFooterPagination // Hide the default pagination
          onRowClick={(params) => {
            const rowProduct: BillItem | undefined = params.row?.product
            const sno = rowProduct?.sno
            if (sno && (pendingItemStatus[sno] || pendingDeleteSno[sno])) return
            setSelectedRow(params.row.id)
            setSelectedProductId(params.row.productId)
          }}
          sx={{
            maxWidth: '100%',
            border: 'none',
            '& .MuiDataGrid-cell': {
              cursor: 'pointer',
              border: 'none',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
            },
            '& .MuiDataGrid-footerContainer': {
              display: 'none', // Hides pagination footer and removes its space
            },
            '& .MuiDataGrid-columnHeader': {
              display: 'none',
            },
            '& .MuiDataGrid-scrollbar': {
              display: 'none',
            },
          }}
        />
      </Stack>

      <Stack flex={1} justifyContent={'flex-end'}>
        {selectedItems?.length > 0 && (
          <CommonBtn variant="contained" onClick={sendAllMarkedItemsToKitchen}>
            Send To Kitchen
          </CommonBtn>
        )}
      </Stack>
      {/* <Stack>

        <DataGrid
          disableColumnResize
          checkboxSelection
          onRowSelectionModelChange={(t, d) => setSelectedItems(Object.values(t))}
          rows={rows}
          columns={columns}
          rowHeight={ROW_HEIGHT}
          disableColumnSorting
          disableColumnFilter
          disableColumnMenu
          pageSizeOptions={[pageSize]} // Disable internal pagination
          hideFooterPagination // Hide the default pagination
          // onRowClick={(params) => {
          //   setSelectedRow(params.row.id)
          //   setSelectedProductId(params.row.productId)
          // }}
          sx={{
            maxWidth: '100%',
            border: 'none',
            '& .MuiDataGrid-cell': {
              cursor: 'pointer',
              border: 'none',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
            },
            '& .MuiDataGrid-footerContainer': {
              display: 'none', // Hides pagination footer and removes its space
            },
            '& .MuiDataGrid-columnHeader': {
              display: 'none',
            },
            '& .MuiDataGrid-scrollbar': {
              display: 'none',
            },
          }}
        />
      </Stack> */}
      {selectedRow !== null && (
        <Box
          sx={{
            position: 'absolute',
            top: `${
              (selectedRow > 5
                ? (selectedRow - 5 * page) * ROW_HEIGHT
                : selectedRow * ROW_HEIGHT) -
              (54 - REDUCE)
            }px`,
            left: 0,
            width: '100%',
            height: `${ROW_HEIGHT}px`,
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            zIndex: 1,
          }}
        >
          {/* Select Dropdown here */}
          {(canShowSendInKitchen(selectedRow) ||
            canShowServed(selectedRow)) && (
            <Select
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
            >
              {canShowSendInKitchen(selectedRow) && (
                <MenuItem value="option1">
                  <CommonBtn
                    variant="contained"
                    startIcon={<Kitchen />}
                    color="info"
                    onClick={() =>
                      senItemToKitchen(KITCHEN_ITEM_STATUS.IN_KITCHEN)
                    }
                  >
                    Send to Kitchen
                  </CommonBtn>
                </MenuItem>
              )}

              {canShowServed(selectedRow) && (
                <MenuItem value="option2">
                  <CommonBtn
                    variant="contained"
                    startIcon={<TableRestaurant />}
                    color="warning"
                    onClick={() => senItemToKitchen(KITCHEN_ITEM_STATUS.SERVED)}
                  >
                    Served
                  </CommonBtn>
                </MenuItem>
              )}
            </Select>
          )}
          {cartItems[selectedRow - 1] &&
            (cartItems[selectedRow - 1]?.kitchInfo?.status ===
              KITCHEN_ITEM_STATUS.IN_CART ||
              cartItems[selectedRow - 1]?.kitchInfo?.status ===
                KITCHEN_ITEM_STATUS.IN_KITCHEN) && (
              <>
                <IconButton
                  aria-label="edit"
                  size="large"
                  color="info"
                  onClick={handleEdit}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </>
            )}
          <IconButton
            aria-label="delete"
            size="large"
            color="error"
            onClick={handleDelete}
          >
            <DeleteIcon fontSize="inherit" />
          </IconButton>

          <IconButton
            aria-label="close"
            size="large"
            color="error"
            onClick={() => setSelectedRow(null)}
          >
            <Close fontSize="inherit" />
          </IconButton>
        </Box>
      )}

      {/* Delete-only panel lock. Absorbs all pointer events on the cart
          while a delete is in flight — deletes rewrite the whole table
          via PUT, and rapid-fire clicks were racing each other and
          restoring just-deleted rows. Kitchen / Served status changes
          go through a per-item endpoint and only spin their own row. */}
      {isDeleting && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            zIndex: 10,
            cursor: 'wait',
          }}
        >
          <CircularProgress size={34} thickness={4} sx={{ color: '#fff' }} />
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
            Removing item…
          </Typography>
        </Box>
      )}
    </Stack>
  )
}

export { ProductsCartItems }
